/**
 * Общие детали «Хозяйства против соседей» и рабочего списка: чтение картины
 * цикла района.
 */

import { useMemo, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../../api/client';
import { useRequest, type RequestResult } from '../../api/useRequest';
import { useAnalysisContext } from '../../analysis/context';
import { InfoPopover } from '../../components/InfoPopover';
import { cycleLabel, defaultCycle } from '../data-source/api/compare';
import { farmMetrics, type FarmMetrics } from '../data-source/api/farmBench';
import './farmBench.css';

export function Titled({ children, info, label }: { children: ReactNode; info: ReactNode; label: string }) {
  return (
    <span className="fb-title">
      {children}
      <InfoPopover icon="info" label={label}>
        {info}
      </InfoPopover>
    </span>
  );
}

/** Цикл района и его картина по хозяйствам — один раз на экран. */
export function useDistrictFarms() {
  const [search] = useSearchParams();
  const { params, asOf: ctxAsOf } = useAnalysisContext();
  const cycles = useRequest(() => api.seasonCycles(params), [params]);
  const items = useMemo(() => cycles.data?.items ?? [], [cycles.data]);
  const fromUrl = search.get('cycle');
  const cycle = fromUrl && items.some((c) => c.cycle === fromUrl) ? fromUrl : defaultCycle(items);
  const cycleRow = items.find((c) => c.cycle === cycle) ?? null;
  const picture = useRequest(
    () =>
      cycle
        ? api.seasonPicture({ ...params, cycle, summary: true, weeks: 0 })
        : Promise.resolve(null),
    [params, cycle],
  );
  const metrics = useMemo<FarmMetrics[]>(() => {
    const summary = picture.data?.summary;
    if (!summary) return [];
    return summary.by_farm.map((r) => farmMetrics(r, summary.water_by_actor));
  }, [picture.data]);
  const synthetic = picture.data?.obligations.measured?.epistemic === 'synthetic_demo';
  /** Дата среза: явная из адреса, иначе горизонт картины. */
  const asOf = ctxAsOf ?? picture.data?.horizon ?? null;
  return {
    params,
    cycles: cycles as RequestResult<unknown>,
    cycle,
    cycleRow,
    cycleItems: items,
    asOf,
    cycleName: cycleRow ? cycleLabel(cycleRow) : null,
    picture,
    metrics,
    synthetic,
    // Картина ещё не пришла — это загрузка, а не «хозяйств нет»: после пустого
    // ответа без цикла запрос идёт в состоянии `refresh` с `data === null`.
    loading:
      cycles.state === 'loading' ||
      picture.state === 'loading' ||
      (cycle !== null && !picture.data && picture.state !== 'error'),
    error: cycles.error ?? picture.error,
  };
}
